import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '../LanguageContext';
import styles from './FirstScreen.module.css';
import ProjectsGallery from '../components/projects/ProjectsGallery';

// Import logos
import logoShenkar from '../images/שנקר.webp';
import logoForbes from '../images/פורבס.webp';
import logoArchitizer from '../images/ארקיזר.webp';
import logoBinyanVeDiyur from '../images/בניין ודיור.webp'
import logoTheMarker from '../images/דה מרקר.webp';
import logoDwell from '../images/דוול.webp';
import logoWalla from '../images/וואלה בית ועיצוב.webp';
import logoMako from '../images/מאקו.webp';
import logoAt from '../images/מגזין את.webp';

// Import project images for carousels
import shilat2 from '../images/בית פרטי בשילת 2.webp';
import shilat5 from '../images/בית פרטי בשילת 5.webp';
import shilat10 from '../images/בית פרטי בשילת 10.webp';
import shilat15 from '../images/בית פרטי בשילת 15.webp';
import migdal3 from '../images/יוקרה במגדל - דירת יוקרה אורבנית 3.webp';
import migdal8 from '../images/יוקרה במגדל - דירת יוקרה אורבנית 8.webp';
import migdal15 from '../images/יוקרה במגדל - דירת יוקרה אורבנית 15.webp';
import migdal22 from '../images/יוקרה במגדל - דירת יוקרה אורבנית 22.webp';
import ramatChen6 from '../images/בית פרטי ברמת חן -בית הפליסה 6.webp';
import ramatChen15 from '../images/בית פרטי ברמת חן -בית הפליסה 15.webp';
import ramatChen28 from '../images/בית פרטי ברמת חן -בית הפליסה 28.webp';
import ramatChen36 from '../images/בית פרטי ברמת חן -בית הפליסה 36.webp';
import image1 from "../images/בית מודרני ואלגנטי בכפר בילו 12.webp"
import image2 from "../images/בית מודרני ואלגנטי בכפר בילו 3.webp"
import image3 from "../images/פנטהאוז תל אביבי - הגוש הגדול 8.webp"
import image4 from "../images/פנטהאוז תל אביבי - הגוש הגדול 6.webp"
import image5 from "../images/פנטהאוז כפרי 4.webp"
import image6 from "../images/פנטהאוז כפרי 1.webp"
import image7 from "../images/בית מודרני ואלגנטי בכפר בילו 34.webp"
import image8 from "../images/בית פרטי בשחור ולבן 20.webp"
import image9 from "../images/דירת בוטיק - הרצליה הילס 21.webp"
import image10 from "../images/דירה תל אביבית בשחקים 11.webp"
import image11 from "../images/בית פרטי כפרי בקרית אונו 35.webp"
import image12 from "../images/בית פרטי בשילת 15.webp"
gsap.registerPlugin(ScrollTrigger);

// תמונות לקרוסלות מהפרויקטים
const leftImages = [
  shilat2,
  migdal3,
   image1,
    image3,
  ramatChen28,
  ramatChen6,
  image5,
  shilat5,
  image7,
  image8,
  image12,
  shilat10,
];

const rightImages = [
  migdal8,
  ramatChen15,
  image2,
   migdal15,
  shilat15,
  image4,
  image9,
  image10,
  ramatChen36,
  image6,
  image11,
  migdal22,
];

// לוגואים אמיתיים - מסודרים לפי חשיבות
const logos = [
  { src: logoForbes, alt: 'Forbes' },
  { src: logoDwell, alt: 'Dwell' },
  { src: logoArchitizer, alt: 'Architizer' },
  { src: logoTheMarker, alt: 'TheMarker' },
  { src: logoMako, alt: 'Mako' },
  { src: logoWalla, alt: 'Walla' },
  { src: logoBinyanVeDiyur, alt: 'בניין ודיור' },
];

// All images to preload
const allImagesToPreload = [
  ...leftImages,
  ...rightImages,
  logoShenkar,
  logoForbes,
  logoArchitizer,
  logoBinyanVeDiyur,
  logoTheMarker,
  logoDwell,
  logoWalla,
  logoMako,
  logoAt,
];

// Luxury Loader Component - Simplified
const LuxuryLoader = ({ progress, isExiting, loaderText }) => {
  const loaderRef = useRef(null);

  useEffect(() => {
    if (isExiting && loaderRef.current) {
      gsap.to(loaderRef.current, {
        opacity: 0,
        duration: 0.8,
        ease: 'power2.inOut',
      });
    }
  }, [isExiting]);

  return (
    <div className={styles.loaderOverlay} ref={loaderRef}>
      <div className={styles.loaderContent}>
        {/* Logo/Brand */}
        <div className={styles.loaderBrand}>
          <span className={styles.loaderBrandText}>NRB</span>
        </div>

        {/* Progress indicator */}
        <div className={styles.loaderProgressContainer}>
          <div className={styles.loaderProgressTrack}>
            <div 
              className={styles.loaderProgressBar} 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className={styles.loaderProgressText}>
            {Math.round(progress)}%
          </span>
        </div>

        {/* Loading text */}
        <p className={styles.loaderText}>
          <span className={styles.loaderTextHebrew}>{loaderText}</span>
        </p>
      </div>
    </div>
  );
};

const NarkisHero = () => {
  const { t, isEnglish } = useTranslation();
  const sectionRef = useRef(null);
  const featuredRef = useRef(null);
  const nameRef = useRef(null);
  const subtitleRef = useRef(null);
  const statsRef = useRef(null);
  const ctaRef = useRef(null);
  const leftCarouselRef = useRef(null);
  const rightCarouselRef = useRef(null);
  const contentRef = useRef(null);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoaderExiting, setIsLoaderExiting] = useState(false);
  
  // Check if mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Preload all images
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = allImagesToPreload.length;
    
    const preloadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          loadedCount++;
          setLoadProgress((loadedCount / totalImages) * 100);
          resolve();
        };
        img.onerror = () => {
          loadedCount++;
          setLoadProgress((loadedCount / totalImages) * 100);
          resolve(); // Continue even if image fails
        };
        img.src = src;
      });
    };

    Promise.all(allImagesToPreload.map(preloadImage))
      .then(() => {
        // Small delay for smooth transition
        setTimeout(() => {
          setIsLoaderExiting(true);
          setTimeout(() => {
            setIsLoading(false);
          }, 800);
        }, 300);
      });
  }, []);

  // Initial entrance animations - runs AFTER loader exits
  useEffect(() => {
    if (isLoading) return;

    const ctx = gsap.context(() => {
      // Set initial states
      gsap.set([
        featuredRef.current,
        nameRef.current,
        subtitleRef.current,
        ctaRef.current
      ], {
        opacity: 0,
        y: 30
      });

      if (statsRef.current?.children) {
        gsap.set(statsRef.current.children, {
          opacity: 0,
          y: 20
        });
      }

      // Create entrance timeline
      const tl = gsap.timeline({
        delay: 0.2 // Small delay after loader exits
      });

      tl.to(featuredRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
      });

      tl.to(nameRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
      }, '-=0.5');

      tl.to(subtitleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: 'power2.out',
      }, '-=0.5');

      if (statsRef.current?.children) {
        tl.to(statsRef.current.children, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power2.out',
        }, '-=0.3');
      }

      tl.to(ctaRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: 'power2.out',
      }, '-=0.2');

    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading]);

  // Cinematic Split - Lighter scrolling effect (Desktop only)
  useEffect(() => {
    if (isLoading) return;

    const mm = gsap.matchMedia();
    
    mm.add("(min-width: 769px)", () => {
      const ctx = gsap.context(() => {
        // Right carousel goes RIGHT - lighter effect
        gsap.to(leftCarouselRef.current, {
          x: '60%',
          opacity: 0.3,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 2,
          },
        });

        // Left carousel goes LEFT - lighter effect
        gsap.to(rightCarouselRef.current, {
          x: '-60%',
          opacity: 0.3,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 2,
          },
        });

        // Content fades out
        gsap.to(contentRef.current, {
          opacity: 0,
          y: -30,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: '30% top',
            end: '70% top',
            scrub: 2,
          },
        });

      }, sectionRef);

      return () => ctx.revert();
    });

    // Mobile - only fade content, NOT carousels
    mm.add("(max-width: 768px)", () => {
      const ctx = gsap.context(() => {
        // Only fade content on mobile, carousels stay visible
        gsap.to(contentRef.current, {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: '30% top',
            end: '70% top',
            scrub: 2,
          },
        });
      }, sectionRef);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, [isLoading]);

  // שכפול מותאם - יותר שכפולים למובייל כדי למלא את כל הרוחב בלי רווחים
  const duplicatedLeft = isMobile 
    ? [...leftImages, ...leftImages, ...leftImages, ...leftImages, ...leftImages, ...leftImages]
    : [...leftImages, ...leftImages, ...leftImages];
  
  const duplicatedRight = isMobile 
    ? [...rightImages, ...rightImages, ...rightImages, ...rightImages, ...rightImages, ...rightImages]
    : [...rightImages, ...rightImages, ...rightImages];
  
  const duplicatedLogos = [...logos, ...logos, ...logos, ...logos];

  return (
    <>
      {/* Show loader while loading */}
      {isLoading && (
        <LuxuryLoader progress={loadProgress} isExiting={isLoaderExiting} loaderText={t.hero.loaderText} />
      )}

      {/* Main content - always rendered but hidden during loading */}
      <section
        className={styles.heroSection}
        ref={sectionRef}
        data-keep-rtl
        style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
      >
        {/* קרוסלה ימנית - עולה */}
        <div className={styles.carouselContainer} ref={leftCarouselRef}>
          <div className={styles.carouselOverlay}></div>
          <div className={styles.carouselBlurTop}></div>
          <div className={styles.carouselBlurBottom}></div>
          <div className={`${styles.carouselTrack} ${styles.carouselLeft}`}>
            {duplicatedLeft.map((src, index) => (
              <div key={`left-${index}`} className={styles.imageWrapper}>
                <img 
                  src={src} 
                  alt="עיצוב פנים נרקיס רובין ברזני"
                  className={styles.carouselImage}
                  loading="eager"
                />
              </div>
            ))}
          </div>
        </div>

        {/* תוכן מרכזי */}
        <div className={styles.content} ref={contentRef}>
          {/* פינות דקורטיביות */}
          <div className={`${styles.decorCorner} ${styles.topRight}`}></div>
          <div className={`${styles.decorCorner} ${styles.bottomLeft}`}></div>

          {/* כפי שהופיעה ב */}
          <div className={styles.featuredSection} ref={featuredRef}>
            <span className={styles.featuredLabel}>{t.hero.featuredLabel}</span>
            <div className={styles.logoCarouselWrapper}>
              {/* Desktop: animated carousel */}
              {!isMobile && (
                <div className={styles.logoCarousel}>
                  {duplicatedLogos.map((logo, index) => (
                    <div key={index} className={styles.logoItem}>
                      <img src={logo.src} alt={logo.alt} className={styles.logoImage} />
                    </div>
                  ))}
                </div>
              )}
              {/* Mobile: static row */}
              {isMobile && (
                <div className={styles.logoRowStatic}>
                  {logos.map((logo, index) => (
                    <div key={index} className={styles.logoItem}>
                      <img src={logo.src} alt={logo.alt} className={styles.logoImage} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* שם באנגלית */}
          <h1 className={styles.name} ref={nameRef}>
            Narkis Rubin Barazani
          </h1>

          {/* סטטיסטיקות עם קווים מפרידים */}
          <div className={styles.stats} ref={statsRef}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{t.hero.stat1Number}</span>
              <span className={styles.statLabel}>{t.hero.stat1Label}</span>
            </div>

            <div className={styles.statDivider}></div>

            <div className={styles.statItem}>
              <span className={styles.statNumber}>{t.hero.stat2Number}</span>
              <span className={styles.statLabel}>{t.hero.stat2Label}</span>
            </div>

            <div className={styles.statDivider}></div>

            <div className={styles.statItem}>
              <span className={styles.statNumber}>{t.hero.stat3Number}</span>
              <span className={styles.statLabel}>{t.hero.stat3Label}</span>
            </div>
          </div>

          {/* כפתור CTA - וואטסאפ */}
          <div className={styles.ctaContainer} ref={ctaRef}>
            <a
              href={`https://wa.me/+972544998929?text=${t.hero.whatsappText}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaButton}
              style={{ textDecoration: 'none' }}
            >
              <span>{t.hero.ctaText}</span>
              <svg className={styles.ctaArrow} viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>

        {/* קרוסלה שמאלית - יורדת */}
        <div className={styles.carouselContainer} ref={rightCarouselRef}>
          <div className={styles.carouselOverlay}></div>
          <div className={styles.carouselBlurTop}></div>
          <div className={styles.carouselBlurBottom}></div>
          <div className={`${styles.carouselTrack} ${styles.carouselRight}`}>
            {duplicatedRight.map((src, index) => (
              <div key={`right-${index}`} className={styles.imageWrapper}>
                <img 
                  src={src} 
                  alt="עיצוב פנים נרקיס רובין ברזני"
                  className={styles.carouselImage}
                  loading="eager"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default NarkisHero;